import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  downloadDeviceReport,
  opteemumSummaryQuery,
  type DeviceOpteemumSummaryPage,
  type OpteemumSummaryTile,
} from '../api/devices'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Opteemum Summary - opteemum/devices/details/deviceSummary.php:
 * sase casute (server sau statie), OS & Application Management, System
 * Health, TuneUp sau Primary service, Export Report, Service health cand
 * masina are rol.
 */

function Tile({ tile }: { tile: OpteemumSummaryTile }) {
  return (
    <div className="col-md-2">
      <div className={`dashboard-stat ${tile.tone}`}>
        <div className="visual">
          <i className={tile.icon}></i>
        </div>
        <div className="details">
          <div
            className={`number number-summary${tile.font24 ? ' font-24' : ''}`}
            style={tile.font24 ? { fontSize: 16, lineHeight: '31px' } : undefined}
            dangerouslySetInnerHTML={{ __html: tile.number }}
          />
          {tile.label !== '' && <div className="desc">{tile.label}</div>}
          {tile.label === '' && <div className="desc"></div>}
        </div>
        <div className="more">{tile.more}</div>
      </div>
    </div>
  )
}

function OsApps({ page }: { page: DeviceOpteemumSummaryPage }) {
  const apps = page.os_apps
  return (
    <div className="col-md-4">
      <div className="row">
        <div className="col-md-12 divider">
          <h4 className="bordered">OS &amp; Application Management</h4>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <table className="mlist-details" width="100%">
            <tbody>
              {apps ? (
                <>
                  <tr>
                    <td>
                      <b>Operating system: </b>
                    </td>
                    <td>{apps.operating_system}</td>
                  </tr>
                  {!apps.is_server && (
                    <>
                      <tr>
                        <td>
                          <b>Office: </b>
                        </td>
                        <td>{apps.office}</td>
                      </tr>
                      <tr>
                        <td>
                          <b>Email client: </b>
                        </td>
                        <td>{apps.default_mail_client}</td>
                      </tr>
                      <tr>
                        <td>
                          <b>Default browser: </b>
                        </td>
                        <td>{apps.default_browser}</td>
                      </tr>
                    </>
                  )}
                  {apps.linux_guard ? (
                    <tr>
                      <td>
                        <b>Access control: </b>
                      </td>
                      <td>
                        SELinux / AppArmor
                        <span className="color-light-grey" style={{ fontSize: 11 }}>
                          {' '}
                          &ndash; not collected yet
                        </span>
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td>
                        <b>Antivirus: </b>
                      </td>
                      <td dangerouslySetInnerHTML={{ __html: apps.antivirus + apps.antivirus_mark }} />
                    </tr>
                  )}
                  <tr>
                    <td>
                      <b>Firewall: </b>
                    </td>
                    <td dangerouslySetInnerHTML={{ __html: apps.firewall + apps.firewall_mark }} />
                  </tr>
                  {!apps.is_server && !apps.linux_guard && (
                    <tr>
                      <td>
                        <b>Antispyware: </b>
                      </td>
                      <td>{apps.antispyware}</td>
                    </tr>
                  )}
                  <tr style={{ backgroundColor: '#dfba49', color: '#fff' }}>
                    <td>
                      <b>OS available updates: </b>
                    </td>
                    <td style={{ fontSize: 14 }}>{apps.os_available_updates}</td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan={7} className="bg-transparent">
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle"></i> No results found.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function Health({ page }: { page: DeviceOpteemumSummaryPage }) {
  const h = page.health_rows
  return (
    <div className="col-md-4">
      <div className="row">
        <div className="col-md-12 divider">
          <h4 className="bordered">System Health</h4>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12 ">
          <table className="mlist-details" width="100%">
            <tbody>
              <tr>
                <td>
                  <b>Processor: </b>
                </td>
                <td>{h.processor}</td>
              </tr>
              <tr>
                <td>
                  <b>Memory Installed: </b>
                </td>
                <td>{h.memory}</td>
              </tr>
              <tr>
                <td>
                  <b>Storage: </b>
                </td>
                <td>{h.storage}</td>
              </tr>
              <tr>
                <td>
                  <b>CPU Temperature: </b>
                </td>
                <td dangerouslySetInnerHTML={{ __html: h.cpu_temp }} />
              </tr>
              <tr>
                <td>
                  <b>Storage Temperature: </b>
                </td>
                <td dangerouslySetInnerHTML={{ __html: h.storage_temp }} />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function TuneUp({ page }: { page: DeviceOpteemumSummaryPage }) {
  const t = page.tuneup
  if (!t) return null
  return (
    <div className="col-md-4">
      <div className="row">
        <div className="col-md-12 divider">
          <h4 className="bordered">TuneUp &amp; Optimisation</h4>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12 ">
          <table className="mlist-details" width="100%">
            <tbody>
              <tr>
                <td>
                  <b>Last OS CleanUp: </b>
                </td>
                <td>{t.last_os}</td>
              </tr>
              <tr>
                <td>
                  <b>Last Apps &amp; Browsing: </b>
                </td>
                <td>{t.last_browsing}</td>
              </tr>
              <tr>
                <td>
                  <b>Last TuneUp &amp; Optimization: </b>
                </td>
                <td>{t.last_tuneup}</td>
              </tr>
              <tr>
                <td>
                  <b>Last Privacy SetUp: </b>
                </td>
                <td>{t.last_privacy}</td>
              </tr>
              <tr>
                <td>
                  <b>Current BootUp Time: </b>
                </td>
                <td>
                  {t.current_boot}
                  {t.current_boot_note !== '' && <span dangerouslySetInnerHTML={{ __html: t.current_boot_note }} />}
                </td>
              </tr>
              <tr>
                <td>
                  <b>Previous BootUp Time: </b>
                </td>
                <td>{t.previous_boot}</td>
              </tr>
              <tr>
                <td>
                  <b>Best BootUp Time: </b>
                </td>
                <td>{t.best_boot}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function Primary({ page }: { page: DeviceOpteemumSummaryPage }) {
  const p = page.primary
  if (!p) return null
  return (
    <div className="col-md-4">
      <div className="row">
        <div className="col-md-12 divider">
          <h4 className="bordered">Primary service</h4>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <table className="mlist-details" width="100%">
            <tbody>
              <tr>
                <td>
                  <b>Service: </b>
                </td>
                <td>{p.service}</td>
              </tr>
              <tr>
                <td>
                  <b>Version: </b>
                </td>
                <td>{p.version}</td>
              </tr>
              <tr>
                <td>
                  <b>Package: </b>
                </td>
                <td>{p.package}</td>
              </tr>
              {p.signals.map((sig) => (
                <tr key={sig.label}>
                  <td>
                    <b>{sig.label}: </b>
                  </td>
                  <td>{sig.display}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function ServiceHealth({ page }: { page: DeviceOpteemumSummaryPage }) {
  const s = page.service
  if (!s) return null
  return (
    <div className="row row-content">
      <div className="col-md-12 divider">
        <h4 className="bordered">
          Service health
          {s.role_label !== '' && (
            <small>
              {' '}
              {s.role_label}
              {s.others.map((o) => (
                <span key={o}> · {o}</span>
              ))}
            </small>
          )}
        </h4>
      </div>
      {s.next_text !== '' && (
        <div className="col-md-12 margin-bottom-10">
          <div className={`alert alert-${s.next_cls}`} style={{ marginBottom: 10 }}>
            <strong>Next:</strong> {s.next_text}
            {s.reasons.length > 0 && (
              <>
                <br />
                <small>{s.reasons.join(' · ')}</small>
              </>
            )}
          </div>
        </div>
      )}
      <div className="col-md-6">
        <h5>Explained patches</h5>
        {s.updates_count > 0 ? (
          <div className={`alert alert-${s.updates_security ? 'danger' : 'warning'}`}>
            <strong>{s.updates_count}</strong> {s.updates_security ? 'security-related' : ''} update(s) for{' '}
            <strong>{s.updates_name}</strong>
            {s.updates_version !== '' && <> ({s.updates_version})</>}.
            {s.updates_cves.length > 0 ? (
              <>
                <br />
                <small>{s.updates_cves.join(', ')}</small>
              </>
            ) : (
              s.updates_detail !== '' && (
                <>
                  <br />
                  <small>{s.updates_detail}</small>
                </>
              )
            )}
          </div>
        ) : (
          <p className="text-muted">No pending updates matched the primary service package.</p>
        )}
        <h5>Recommendations</h5>
        {s.optimizations.length > 0 ? (
          <ul className="list-unstyled">
            {s.optimizations.map((opt) => (
              <li key={opt.title} className="margin-bottom-10">
                <span className={`label label-${opt.cls}`}>{opt.title}</span>
                <div className="font-12 color-dark-grey">{opt.detail}</div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted">No optimization recommendations right now.</p>
        )}
      </div>
      <div className="col-md-6">
        <h5>
          Configuration review
          {s.config_score !== '' && (
            <>
              {' '}
              <small className={`label label-${s.config_score_badge}`}>{s.config_score}</small>
            </>
          )}
        </h5>
        {s.config_items.length > 0 ? (
          <table className="table table-striped table-hover" width="100%">
            <thead>
              <tr>
                <th>Path</th>
                <th width={100}>Status</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {s.config_items.map((item) => (
                <tr key={item.path}>
                  <td>
                    <code>{item.path}</code>
                  </td>
                  <td>
                    <span className={`label label-${item.badge}`}>{item.status}</span>
                  </td>
                  <td>{item.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-muted">No config facts yet — update the Linux agent to 10.0.38+ for SSH and listen probes.</p>
        )}
      </div>
    </div>
  )
}

export function DeviceOpteemumSummary({ id }: ScreenProps) {
  const { data, error } = useQuery(opteemumSummaryQuery(id))
  const [exporting, setExporting] = useState(false)
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  return (
    <>
      <div className="row">
        {data.tiles.map((tile, i) => (
          <Tile key={i} tile={tile} />
        ))}
      </div>
      <div className="row row-content">
        <OsApps page={data} />
        <Health page={data} />
        {data.is_server ? <Primary page={data} /> : <TuneUp page={data} />}
      </div>
      <div className="row row-content">
        <div className="col-md-4">
          <div className="row">
            <div className="col-md-12 divider">
              <h4 className="bordered">Device Report</h4>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12 ">
              {/* Opteemum::deviceReport - GET /devices/{id}/opteemum-report.pdf, salvat in browser. */}
              <button
                className="btn btn-sm btn-primary generateDeviceReport"
                type="button"
                disabled={exporting}
                onClick={() => {
                  setExporting(true)
                  downloadDeviceReport(id)
                    .catch((err: unknown) => toast(err instanceof Error ? err.message : 'Report could no be generated', 'Error', 'error'))
                    .finally(() => setExporting(false))
                }}
              >
                <span className="fas fa-file-pdf"></span> Export Report
              </button>
            </div>
          </div>
        </div>
      </div>
      <ServiceHealth page={data} />
    </>
  )
}
